import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Sparkles, FileText, AlertOctagon, CheckSquare, ListTodo, ClipboardList, Download } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SimpleMD from "@/components/app/SimpleMD";
import { toast } from "sonner";

const HEALTH_BG = { green: "bg-green-600", amber: "bg-amber-500", red: "bg-red-600" };
const HEALTH_LABEL = { green: "On track", amber: "At risk", red: "Off track" };

const Stat = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-[#0D1117] p-5">
    <div className="flex items-center justify-between">
      <Icon className="w-4 h-4 text-gray-400" strokeWidth={1.5} />
      <div className={`text-[10px] uppercase tracking-[0.2em] ${accent || "text-gray-500"}`}>{label}</div>
    </div>
    <div className="font-display font-black text-3xl tracking-tighter mt-3">{value}</div>
  </div>
);

const ProjectReport = () => {
  const { projectId } = useParams();
  const [report, setReport] = useState(null);
  const [docTypes] = useState([
    { key: "status_report", name: "Status Report" },
    { key: "risk_register", name: "Risk Register" },
    { key: "charter", name: "Project Charter" },
    { key: "scope_statement", name: "Scope Statement" },
  ]);
  const [docType, setDocType] = useState("status_report");
  const [generating, setGenerating] = useState(false);
  const [narrative, setNarrative] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/reports/project/${projectId}`);
    setReport(data);
  };
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line

  const generateNarrative = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/generate-doc", { project_id: projectId, doc_type: docType });
      setNarrative(data);
      toast.success("AI report generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate report");
    } finally {
      setGenerating(false);
    }
  };

  if (!report) return <div className="p-12 text-sm text-gray-500">Loading project report…</div>;

  const { project } = report;

  return (
    <div>
      <PageHeader
        backTo={`/workspace/${projectId}`}
        eyebrow={`Report · ${project.methodology}`}
        title={project.name}
        description="A snapshot of where this project stands today."
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="rounded-none border-gray-300 text-gray-600 hover:text-gray-900 bg-transparent"
              onClick={async () => {
                try {
                  const token = localStorage.getItem("token");
                  const BASE = process.env.REACT_APP_API_URL || "";
                  const res = await fetch(`${BASE}/api/reports/project/${projectId}/pdf`, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  if (!res.ok) throw new Error("Export failed");
                  const blob = await res.blob();
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(blob);
                  a.download = `${project.name.replace(/\s+/g, "-")}-report.pdf`;
                  a.click();
                  URL.revokeObjectURL(a.href);
                } catch { toast.error("PDF export failed"); }
              }}
              data-testid="download-report-pdf-btn"
            >
              <Download className="w-4 h-4 mr-1" /> PDF
            </Button>
            <Link to={`/workspace/${projectId}`}>
              <Button variant="outline" className="rounded-none border-gray-300 text-gray-600 hover:text-gray-900 bg-transparent" data-testid="back-to-project-from-report">
                <ArrowLeft className="w-4 h-4 mr-1" /> Project
              </Button>
            </Link>
          </div>
        }
      />

      <div className="px-5 sm:px-8 lg:px-12 py-8 space-y-8">
        {/* Health banner */}
        <div className={`border-l-4 border-[#1F2937] bg-[#030712] p-6 flex items-center justify-between`} data-testid="project-health-banner">
          <div className="flex items-center gap-4">
            <span className={`w-3 h-3 ${HEALTH_BG[project.health] || "bg-gray-400"}`} />
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-gray-500">Current health</div>
              <div className="font-display font-bold text-2xl tracking-tight mt-1">{HEALTH_LABEL[project.health] || "—"}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-[0.25em] text-gray-500">Status</div>
            <div className="font-display font-bold text-2xl tracking-tight mt-1 capitalize">{(project.status || "").replace("_", " ")}</div>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-[#1F2937] border border-[#1F2937]">
          <Stat icon={CheckSquare} label="Completion" value={`${report.completion_rate}%`} accent="text-[#002FA7]" />
          <Stat icon={ListTodo} label="Tasks" value={report.total_tasks} />
          <Stat icon={AlertOctagon} label="Open RAID" value={report.raid_open} accent={report.raid_critical > 0 ? "text-red-600" : "text-gray-500"} />
          <Stat icon={FileText} label="Documents" value={report.docs_count} />
          <Stat icon={ClipboardList} label="Meetings" value={report.meetings_count} />
        </div>

        {/* Task & RAID breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#1F2937] border border-[#1F2937]">
          <div className="bg-[#0D1117] p-6">
            <div className="text-[11px] uppercase tracking-[0.25em] text-gray-500 mb-4">Task velocity</div>
            <div className="space-y-3">
              {(report.task_status && typeof report.task_status === "object" ? Object.entries(report.task_status) : []).map(([k, v]) => {
                const pct = report.total_tasks ? Math.round((v / report.total_tasks) * 100) : 0;
                const color = k === "done" ? "bg-[#002FA7]" : k === "in_progress" ? "bg-blue-400" : "bg-gray-300";
                return (
                  <div key={k}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="capitalize">{k.replace("_", " ")}</span>
                      <span className="font-mono text-slate-300">{v} · {pct}%</span>
                    </div>
                    <div className="h-2 bg-[#111827] mt-1">
                      <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-[#0D1117] p-6">
            <div className="text-[11px] uppercase tracking-[0.25em] text-gray-500 mb-4">RAID exposure</div>
            <div className="space-y-3">
              {(report.raid_by_type && typeof report.raid_by_type === "object" ? Object.entries(report.raid_by_type) : []).map(([k, v]) => {
                const max = Math.max(1, ...(report.raid_by_type && typeof report.raid_by_type === "object" ? Object.values(report.raid_by_type) : [1]));
                const pct = Math.round((v / max) * 100);
                const color = k === "risk" ? "bg-red-500" : k === "issue" ? "bg-orange-500" : k === "dependency" ? "bg-purple-500" : "bg-blue-500";
                return (
                  <div key={k}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="capitalize">{k}</span>
                      <span className="font-mono text-slate-300">{v}</span>
                    </div>
                    <div className="h-2 bg-[#111827] mt-1">
                      <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
              {report.raid_critical > 0 && (
                <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2">
                  ⚠ {report.raid_critical} critical-impact item{report.raid_critical > 1 ? "s" : ""} open
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action items rollup */}
        {report.open_actions?.length > 0 && (
          <div className="border border-[#1F2937]" data-testid="open-actions-rollup">
            <div className="px-6 py-3 border-b border-[#1F2937] text-xs uppercase tracking-[0.25em] text-slate-400">
              Open meeting actions ({report.open_actions.length})
            </div>
            <table className="w-full text-sm">
              <thead className="bg-[#030712] text-[10px] uppercase tracking-[0.2em] text-slate-400">
                <tr>
                  <th className="text-left p-3 font-semibold">Task</th>
                  <th className="text-left p-3 font-semibold">Owner</th>
                  <th className="text-left p-3 font-semibold">Due</th>
                  <th className="text-left p-3 font-semibold">From</th>
                </tr>
              </thead>
              <tbody>
                {report.open_actions.map((a, i) => (
                  <tr key={`${a.task}-${i}`} className="border-t border-[#1F2937]">
                    <td className="p-3">{a.task}</td>
                    <td className="p-3 text-slate-300">{a.owner || "—"}</td>
                    <td className="p-3 font-mono text-slate-300">{a.due || "—"}</td>
                    <td className="p-3 text-gray-500">{a.meeting}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* AI narrative */}
        <div className="border border-[#1F2937]">
          <div className="px-6 py-3 border-b border-[#1F2937] flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.25em] text-slate-400">AI executive narrative</div>
            <div className="flex items-center gap-2">
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger className="rounded-none h-9 w-44" data-testid="narrative-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {docTypes.map((t) => <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={generateNarrative} disabled={generating} className="rounded-none bg-[#002FA7] hover:bg-[#002480]" data-testid="generate-narrative-btn">
                <Sparkles className="w-4 h-4 mr-1" /> {generating ? "Generating…" : "Generate"}
              </Button>
            </div>
          </div>
          <div className="p-6 bg-[#030712]">
            {narrative ? (
              <div className="bg-[#0D1117] border border-[#1F2937] p-6 max-w-3xl">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#002FA7]">{narrative.doc_type.replace("_", " ")}</div>
                <h3 className="font-display font-black text-2xl tracking-tighter mt-2">{narrative.title}</h3>
                <div className="mt-4 text-sm text-slate-100"><SimpleMD text={narrative.content} /></div>
              </div>
            ) : (
              <div className="text-sm text-gray-500">Generate an AI-written executive narrative for this project.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectReport;
